import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Text,
  TextInput,
  createStyles,
  rem,
} from "@mantine/core";
import ActionButtons from "./ActionButtons";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg";
import { useSelector } from "react-redux";
import { getAllTimezonesWithAbbr } from "../../utils";
import {
  searchAddress,
  getAddressByPlaceId,
} from "../../services/dashboard";

const useStyles = createStyles((theme) => ({
  addressDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 100,
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    maxHeight: 200,
    overflowY: "auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  addressItem: {
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: 13,
    "&:hover": {
      background: "#FFF8E7",
    },
  },
  readOnlyAddress: {
    padding: "10px 14px",
    background: "#f8f9fa",
    borderRadius: 8,
    border: "1px solid #e0e0e0",
    fontSize: 14,
    color: "#495057",
    minHeight: 42,
    display: "flex",
    alignItems: "center",
  },
}));

export const FacilitySettingsTab = ({
  mode,
  form,
  handleSave,
  handleCancel,
  loading,
  ...data
}) => {
  const { classes } = useStyles();
  const [noClockIn, setNoClockIn] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const debounceRef = useRef(null);

  const { allFacilityTypes, loadingAddFacility } = useSelector(
    (state) => state.facility
  );
  const DropDownArrow = (
    <DropDownArrowSvg
      color={"#FBAA0D"}
      style={{ width: rem(1), height: rem(1) }}
    />
  );

  const facilityData = data?.data;
  const loginLocationType = form?.values?.loginLocationType;

  useEffect(() => {
    setNoClockIn(
      !form?.values?.checkInType || form?.values?.checkInType === "no-clockin"
        ? true
        : false
    );
    if (!form?.values?.checkInType) {
      form.setFieldValue("checkInType", "no-clockin");
    }
  }, []);

  // No EVV auto-set needed — login location is independent of EVV

  const handleNoClockInChange = (event) => {
    const checked = event.currentTarget.checked;
    setNoClockIn(checked);
    if (checked) {
      form.setFieldValue("checkInType", "no-clockin");
    } else {
      form.setFieldValue("checkInType", null);
    }
  };

  // Address autocomplete for custom location
  const handleCustomAddressSearch = (value) => {
    form.setFieldValue("loginLocationAddress", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await searchAddress(value);
        setAddressSuggestions(results || []);
        setShowSuggestions(true);
      } catch (e) {
        setAddressSuggestions([]);
      }
      setAddressLoading(false);
    }, 400);
  };

  const handleSelectCustomAddress = async (suggestion) => {
    setShowSuggestions(false);
    setAddressLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const components = placeDetails?.result?.address_components || [];
      const geo = placeDetails?.result?.geometry?.location;

      let streetNumber = "";
      let route = "";
      let city = "";
      let state = "";
      let zipcode = "";

      for (const comp of components) {
        if (comp.types.includes("street_number")) streetNumber = comp.long_name;
        if (comp.types.includes("route")) route = comp.long_name;
        if (comp.types.includes("locality")) city = comp.long_name;
        if (comp.types.includes("administrative_area_level_1")) state = comp.short_name;
        if (comp.types.includes("postal_code")) zipcode = comp.long_name;
      }

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      form.setFieldValue("loginLocationAddress", streetAddress || suggestion.formatted_address);
      form.setFieldValue("loginLocationCity", city);
      form.setFieldValue("loginLocationState", state);
      form.setFieldValue("loginLocationZipcode", zipcode);
      form.setFieldValue("loginLocationLat", geo?.lat || null);
      form.setFieldValue("loginLocationLng", geo?.lng || null);
    } catch (e) {
      form.setFieldValue("loginLocationAddress", suggestion.formatted_address);
    }
    setAddressLoading(false);
  };

  // Build facility address string for display
  const facilityAddress = [
    facilityData?.address,
    facilityData?.city || facilityData?.addressLineTwo || facilityData?.address2,
    facilityData?.state,
    facilityData?.zipcode,
  ]
    .filter(Boolean)
    .join(", ");

  // Build custom address string for display
  const customAddressDisplay = [
    form?.values?.loginLocationAddress,
    form?.values?.loginLocationCity,
    form?.values?.loginLocationState,
    form?.values?.loginLocationZipcode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSave)}>
        <Box>
          <SimpleGrid
            cols={2}
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
            sx={{ paddingTop: 20 }}
          >
            <Select
              id="facility-type"
              searchable
              size="md"
              radius="md"
              label="Facility Type"
              data={
                allFacilityTypes?.ftypes?.map((val) => ({
                  value: val?.id,
                  label: val?.name,
                })) || []
              }
              rightSection={DropDownArrow}
              placeholder="Select Facility Type"
              {...form.getInputProps("type")}
              disabled={mode === "view"}
            />
            <Flex direction="column" gap={10}>
              <Select
                id="clock-in-type"
                searchable
                size="md"
                radius="md"
                label="Clock-in Type"
                data={[
                  { value: "facility", label: "Facility" },
                  { value: "client", label: "Client" },
                ]}
                rightSection={DropDownArrow}
                placeholder="Select Clock-in Type"
                {...form.getInputProps("checkInType")}
                disabled={mode === "view" || noClockIn}
              />
              <Checkbox
                color="gray"
                label="No Clock-in"
                checked={noClockIn}
                onChange={handleNoClockInChange}
                disabled={mode === "view"}
              />
            </Flex>
          </SimpleGrid>

          {/* Login Location Setting */}
          <SimpleGrid
            cols={2}
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
            sx={{ paddingTop: 20 }}
          >
            <Select
              id="login-location"
              size="md"
              radius="md"
              label="Login Location"
              data={[
                { value: "anywhere", label: "Allow login anywhere" },
                { value: "facility", label: "Facility" },
                { value: "custom", label: "Location" },
              ]}
              rightSection={DropDownArrow}
              placeholder="Select Login Location"
              {...form.getInputProps("loginLocationType")}
              disabled={mode === "view"}
            />
            {loginLocationType === "facility" && (
              <Box>
                <Text size="sm" weight={500} mb={4}>
                  Facility Address
                </Text>
                <Box className={classes.readOnlyAddress}>
                  {facilityAddress || (
                    <Text color="dimmed" size="sm">
                      No facility address set
                    </Text>
                  )}
                </Box>
              </Box>
            )}
            {loginLocationType === "custom" && (
              <Box sx={{ position: "relative" }}>
                <TextInput
                  size="md"
                  radius="md"
                  label="Login Location Address"
                  placeholder="Search for an address..."
                  value={form?.values?.loginLocationAddress || ""}
                  onChange={(e) =>
                    handleCustomAddressSearch(e.currentTarget.value)
                  }
                  disabled={mode === "view"}
                  rightSection={addressLoading ? <Loader size="xs" /> : null}
                />
                {showSuggestions && addressSuggestions.length > 0 && (
                  <Box className={classes.addressDropdown}>
                    {addressSuggestions.map((s, i) => (
                      <Box
                        key={i}
                        className={classes.addressItem}
                        onClick={() => handleSelectCustomAddress(s)}
                      >
                        {s.formatted_address}
                      </Box>
                    ))}
                  </Box>
                )}
                {form?.values?.loginLocationCity && (
                  <Text size="xs" color="dimmed" mt={4}>
                    {customAddressDisplay}
                  </Text>
                )}
              </Box>
            )}
          </SimpleGrid>

          {/* EVV Settings */}
          <SimpleGrid
            cols={2}
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
            sx={{ paddingTop: 20 }}
          >
            <Select
              id="evv-required"
              searchable
              size="md"
              radius="md"
              label="EVV Required"
              data={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
              rightSection={DropDownArrow}
              placeholder="Select EVV Required"
              {...form.getInputProps("evvRequired")}
              disabled={mode === "view"}
            />
            <NumberInput
              size="md"
              radius="md"
              label="EVV Max Distance (miles)"
              placeholder="0.25"
              min={0.1}
              step={0.05}
              precision={2}
              {...form.getInputProps("evvMaxDistance")}
              disabled={mode === "view" || form.values.evvRequired !== "yes"}
            />
          </SimpleGrid>

          {(loginLocationType === "facility" || loginLocationType === "custom") && (
            <Text size="xs" color="dimmed" mt={4}>
              Staff must be within the EVV max distance of the selected location to log in.
            </Text>
          )}

          <SimpleGrid
            cols={2}
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
            sx={{ paddingTop: 20 }}
          >
            <Select
              label="Timezone"
              id="staff-Role"
              disabled={mode === "view"}
              searchable
              size="md"
              radius="md"
              placeholder="Select Timezone"
              rightSection={DropDownArrow}
              data={getAllTimezonesWithAbbr()}
              {...form.getInputProps("timeZone")}
            />
            <Select
              id="two-factor-auth"
              searchable
              size="md"
              radius="md"
              label="Required Two Factor Authentication"
              data={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              rightSection={DropDownArrow}
              placeholder="Select two factor authentication"
              {...form.getInputProps("twoFactorAuth")}
              disabled={mode === "view"}
            />
          </SimpleGrid>
          <SimpleGrid
            cols={2}
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
            sx={{ paddingTop: 20 }}
          >
            <Select
              id="skai-access"
              searchable
              size="md"
              radius="md"
              label="SkAi Access"
              data={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              rightSection={DropDownArrow}
              placeholder="Select SkAi Access"
              {...form.getInputProps("skaiAccess")}
              disabled={mode === "view"}
            />
            <Select
              id="staff-note-edit-time"
              searchable
              size="md"
              radius="md"
              label="Staff Note Edit Time"
              data={[
                { value: "1d", label: "1 Day" },
                { value: "3d", label: "3 Day" },
                { value: "forever", label: "Forever" },
              ]}
              rightSection={DropDownArrow}
              placeholder="Select staff Edit Time"
              {...form.getInputProps("staffnoteEditTime")}
              disabled={mode === "view"}
            />
            <Select
              id="staff-note-edit-task-time"
              searchable
              size="md"
              radius="md"
              label="Staff Task Edit Time"
              data={[
                { value: "1d", label: "1 Day" },
                { value: "3d", label: "3 Day" },
              ]}
              rightSection={DropDownArrow}
              placeholder="Select Task Edit Time"
              {...form.getInputProps("staffTaskEditTime")}
              disabled={mode === "view"}
            />
          </SimpleGrid>
        </Box>

        {(mode === "add" || mode === "edit") && (
          <Box mt={40}>
            <Divider my={10} />
            <ActionButtons
              onCancel={handleCancel}
              type="submit"
              loading={loading}
              isFacility={true}
            />
          </Box>
        )}
      </form>
    </Box>
  );
};
